import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../../public/auth.service';
import { ProfileService } from '../../profile/profile.service';
import { normalizeRoutePath } from './membership-access';
import { isCurrentPathAllowedForTrackerState, resolvePostLoginDestination } from './onboarding-routing';
import {
  hasAuthEmail,
  hasAuthEmailBypass,
  hasAuthPhone,
  hasAuthPhoneBypass,
  isAuthEmailVerified,
  isAuthPhoneVerified,
} from './auth-user-flags';

/**
 * Enforces membership wall for authenticated users on main product routes.
 * Anonymous users pass through unchanged.
 */
@Injectable()
export class MembershipGuard implements CanActivate {
  constructor(
    private auth: AuthService,
    private profileService: ProfileService,
    private router: Router
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    if (!this.auth.hasToken()) {
      return true;
    }

    const path = normalizeRoutePath(state.url);
    const user = this.auth.user$.value || this._userFromStorage();
    if (!user) {
      return true;
    }

    if (this.needsEmailVerification(user)) {
      if (this.isEmailFlowPath(path)) {
        return true;
      }
      const hasEmail = hasAuthEmail(user);
      return this.router.parseUrl(hasEmail ? '/verify-email' : '/add-email');
    }

    if (this.needsPhoneVerification(user)) {
      if (this.isPhoneFlowPath(path)) {
        return true;
      }
      const hasPhone = hasAuthPhone(user);
      return this.router.parseUrl(hasPhone ? '/verify-phone-number' : '/add-phone');
    }

    return this.profileService.ensureProfileTrackerLoaded().pipe(
      map((tracker) => {
        if (isCurrentPathAllowedForTrackerState(state.url, user, tracker)) {
          return true;
        }
        const dest = resolvePostLoginDestination(user, tracker);
        if (!dest) {
          return true;
        }
        const tree = this.router.parseUrl(dest.path);
        if (dest.queryParams) {
          tree.queryParams = { ...tree.queryParams, ...dest.queryParams };
        }
        return tree;
      })
    );
  }

  private needsEmailVerification(user: any): boolean {
    if (!user) {
      return false;
    }
    if (hasAuthEmailBypass(user)) {
      return false;
    }
    if (!hasAuthEmail(user)) {
      return true;
    }
    return !isAuthEmailVerified(user);
  }

  private needsPhoneVerification(user: any): boolean {
    if (!user || hasAuthPhoneBypass(user)) {
      return false;
    }
    return !hasAuthPhone(user) || !isAuthPhoneVerified(user);
  }

  private isEmailFlowPath(path: string): boolean {
    return ['/add-email', '/change-email', '/verify-email'].some(
      (p) => path === p || path.startsWith(p + '/')
    );
  }

  private isPhoneFlowPath(path: string): boolean {
    return ['/add-phone', '/change-phone', '/verify-phone-number'].some(
      (p) => path === p || path.startsWith(p + '/')
    );
  }

  /** Hydrate user for guard when async profile load has not completed yet. */
  private _userFromStorage(): any {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}
